import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { authApi } from '@/services/api/auth.api';

export const ClaimsStatus: React.FC = () => {
  const { user, userClaims, refreshUserClaims } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const hasClaims = !!(userClaims?.role && userClaims?.tenantId);

  useEffect(() => {
    // Auto-retry if no claims after a delay
    if (!hasClaims && user && retryCount < 3) {
      const timer = setTimeout(async () => {
        await handleRefresh();
        setRetryCount(prev => prev + 1);
      }, 3000 * (retryCount + 1)); // Exponential backoff

      return () => clearTimeout(timer);
    }
  }, [hasClaims, user, retryCount]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // First try to refresh via API
      const token = await user?.getIdToken();
      if (token) {
        try {
          await authApi.refreshClaims(token);
        } catch (error) {
          console.error('API refresh failed:', error);
        }
      }
      
      // Then force a full token refresh
      const success = await refreshUserClaims();
      
      if (!success) {
        // If that fails, try a manual token refresh
        await user?.getIdToken(true);
        window.location.reload(); // Last resort
      }
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!user) return null;

  return (
    <div className={`rounded-lg p-4 ${hasClaims ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
      <div className="flex items-start space-x-3">
        {hasClaims ? (
          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
        ) : (
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
        )}
        
        <div className="flex-1">
          <h3 className={`font-medium ${hasClaims ? 'text-green-900' : 'text-amber-900'}`}>
            {hasClaims ? 'Authentication Complete' : 'Finalizing Your Account Setup'}
          </h3>
          
          <div className="mt-1 text-sm space-y-1">
            {hasClaims ? (
              <>
                <p className="text-green-700">Your account is ready to use.</p>
                <p className="text-green-600">
                  Role: <span className="font-medium">{userClaims.role}</span>
                </p>
              </>
            ) : (
              <>
                <p className="text-amber-700">
                  Your account is being configured. This usually completes within a few seconds.
                </p>
                {retryCount > 0 && (
                  <p className="text-amber-600 text-xs">
                    Retry attempt {retryCount}/3...
                  </p>
                )}
              </>
            )}
          </div>
          
          {!hasClaims && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Permissions'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};